import styled from 'styled-components';
import { InAppContentDefinition, Position } from 'modules/graphql/types';
import { cspTag, IframeFullDoc } from 'modules/secureIframe';
import { colors } from 'ui/theme';
import { removeScriptFullDoc } from 'utils/script';

import { inAppWrapper, Overlay, positionerBase, StyledInAppContainer } from './styles';
import { TemplateEditViews } from '../../common/types';
import { useInAppPreviewContext } from '../context/InAppPreviewContext';
import { useQueryParamContext } from '../../context/QueryParamContext';

import { ANIMATION, IN_APP_VIEW_OPTIONS } from '../constants';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { InboxPreview } from './InboxPreview';
import { RokuPreview } from './RokuPreview';
import { PreviewEmptyState } from './PreviewEmptyState';
import { getPositionFromInAppDisplaySetting } from '../utils';
import { generateAnimationStyles, generatePositionStyles } from './styleGenerators';

export type AnimationStyleProps = {
  animation: ANIMATION | null;
  position: Position;
  viewOption: IN_APP_VIEW_OPTIONS;
};

const IframeWithAnimation = styled(IframeFullDoc)<AnimationStyleProps>`
  ${({ animation, position, viewOption }) =>
    animation && generateAnimationStyles(animation, position, viewOption)}
`;

type Props = {
  animation: ANIMATION | null;
  selectedViewOption: IN_APP_VIEW_OPTIONS;
};

export const Preview = ({ animation, selectedViewOption }: Props) => {
  const { hasContent, liveTemplate } = useEditableFieldsContext();
  const { messageHtml, previewError: hasPreviewError } = useInAppPreviewContext();
  const { view } = useQueryParamContext();
  const usePopulatedField = view === TemplateEditViews.preview;
  const inAppData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const { hex, alpha } = inAppData.inAppDisplaySettings?.bgColor || {};
  const position = getPositionFromInAppDisplaySetting(inAppData.inAppDisplaySettings);
  const htmlContent = usePopulatedField ? messageHtml : inAppData.html || '';

  const getPreview = (): JSX.Element => {
    if (selectedViewOption === IN_APP_VIEW_OPTIONS.inbox)
      return <InboxPreview usePopulatedField={usePopulatedField} />;

    if (selectedViewOption === IN_APP_VIEW_OPTIONS.roku) return <RokuPreview />;

    if (!hasContent || !htmlContent || (hasPreviewError && view === TemplateEditViews.preview))
      return <PreviewEmptyState />;

    return (
      <StyledInAppContainer
        data-test={`template-editor-${selectedViewOption}-preview`}
        $option={selectedViewOption}
      >
        <div style={inAppWrapper}>
          <Overlay style={{ backgroundColor: hex || colors.white, opacity: alpha ?? '0%' }} />
          <IframeWithAnimation
            animation={animation}
            position={position}
            style={{
              ...positionerBase,
              ...generatePositionStyles(selectedViewOption === IN_APP_VIEW_OPTIONS.web)[position],
            }}
            viewOption={selectedViewOption}
          >
            {removeScriptFullDoc(htmlContent, cspTag)}
          </IframeWithAnimation>
        </div>
      </StyledInAppContainer>
    );
  };

  return getPreview();
};

export default Preview;
